const BLOCKED_LOGIN_PATHS = new Set(["/login", "/sign-up", "/access-denied"])

// The docs site signs readers in with this login page and is the only other origin a login may
// return to. Any other absolute target falls back to "/", so the page cannot redirect elsewhere.
const DOCS_SITE_ORIGIN = "https://docs.ortelian.com"

export function readLoginRedirectUrlParam(search: string): string | null {
  return readRedirectUrlFromQuery(stripLeading(search, "?"))
}

/** Keep login returns on this application or the docs site, and away from authentication loops. */
export function resolveLoginRedirectUrl(redirectUrl: string | null | undefined): string {
  const target = redirectUrl?.trim()
  if (!target) return "/"

  if (target.startsWith("//") || target.includes("\\")) return "/"
  if (target.startsWith("/")) return sanitizeRelativeRedirect(target)
  return docsSiteRedirect(target) ?? "/"
}

function docsSiteRedirect(target: string): string | null {
  try {
    const url = new URL(target)
    return url.origin === DOCS_SITE_ORIGIN && !url.username && !url.password ? url.toString() : null
  } catch {
    return null
  }
}

/** The login page that returns to `target` after the next sign-in. */
export function loginPathReturningTo(target: string): string {
  const safeTarget = resolveLoginRedirectUrl(target)
  return safeTarget === "/" ? "/login" : `/login?redirect_url=${encodeURIComponent(safeTarget)}`
}

/** True when the request query carries a redirect_url the server can see (not a hash fragment). */
export function hasQueryLoginRedirect(
  searchParams: Record<string, string | string[] | undefined> | undefined,
): boolean {
  const value = searchParams?.redirect_url
  const target = Array.isArray(value) ? value[0] : value
  return Boolean(target?.trim())
}

export function readQueryLoginRedirectUrl(
  searchParams: Record<string, string | string[] | undefined> | undefined,
): string | null {
  const value = searchParams?.redirect_url
  const target = Array.isArray(value) ? value[0] : value
  const trimmed = target?.trim()
  return trimmed ? trimmed : null
}

function readRedirectUrlFromQuery(query: string): string | null {
  if (!query) return null
  try {
    return new URLSearchParams(query).get("redirect_url")?.trim() || null
  } catch {
    return null
  }
}

function sanitizeRelativeRedirect(target: string): string {
  try {
    const url = new URL(target, "https://sigma-labs.local")
    if (url.pathname.startsWith("//") || BLOCKED_LOGIN_PATHS.has(url.pathname)) return "/"
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return "/"
  }
}

function stripLeading(value: string, prefix: string): string {
  return value.startsWith(prefix) ? value.slice(prefix.length) : value
}
